import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { toast } from "@/components/ui/use-toast";
import { getPanelBrandSlug } from "@/config/config";
import { useI18n } from "@/context/i18n";
import { queryClient } from "@/lib/api";

import * as productsApi from "./products.api";
import { isInfinityAssistanceProduct } from "./products.assistance-variant";
import {
  assistanceSchema,
  autoSchema,
  baseProductSchema,
  groupLifeSchema,
  personalAccidentSchema,
  soulhubAssistanceInsuranceSchema,
  TProductFormData,
} from "./products.schema";
import { EProductType, TProduct, TVigencyType } from "./products.type";
import { normalizeVehicleCommercialFromApi } from "./schemas";
import {
  creditProductFormSchema,
  mergeCreditProductConfigFromApi,
} from "./schemas/credit-product.schema";
import { theftInsuranceSchema } from "./schemas/theft-insurance.schema";
import { warrantyInsuranceSchema } from "./schemas/warranty-insurance.schema";

export interface IUseProductEdit {
  product: TProduct | undefined;
  isLoading: boolean;
  isError: boolean;
  updateProduct: (data: TProductFormData) => void;
  productForm: UseFormReturn<TProductFormData>;
}

export const useProductEdit = (productId: string): IUseProductEdit => {
  const { t } = useI18n();
  const navigate = useNavigate();

  /** Evita `reset` em cada refetch do React Query (nova referência de `product`), que apagava o formulário à medida que o utilizador editava. */
  const lastHydratedServerKeyRef = React.useRef<string | null>(null);

  const {
    data: product,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["product", productId],
    queryFn: () => productsApi.getProduct(productId),
    enabled: !!productId,
  });

  React.useEffect(() => {
    lastHydratedServerKeyRef.current = null;
  }, [productId]);

  const getSchemaByType = React.useCallback((type?: EProductType | null) => {
    switch (type) {
      case EProductType.INSURANCE_AP:
        return personalAccidentSchema;
      case EProductType.INSURANCE_VG:
        return groupLifeSchema;
      case EProductType.INSURANCE_ASSISTANCE:
        if (isInfinityAssistanceProduct(product)) {
          return assistanceSchema;
        }
        return getPanelBrandSlug() === "soulhub"
          ? soulhubAssistanceInsuranceSchema
          : assistanceSchema;
      case EProductType.AUTO:
      case EProductType.MOTO:
        return autoSchema;
      case EProductType.CREDIT:
        return creditProductFormSchema;
      case EProductType.WARRANTY_INSURANCE:
        return warrantyInsuranceSchema;
      case EProductType.THEFT_INSURANCE:
        return theftInsuranceSchema;
      default:
        return baseProductSchema;
    }
  }, [product]);

  const productForm = useForm<TProductFormData>({
    resolver: (async (values, context, options) => {
      const typeToUse =
        (product?.type as EProductType) ||
        ((values as { type?: EProductType }).type as EProductType | undefined);
      const schema = getSchemaByType(typeToUse);
      return (zodResolver(schema) as any)(values, context, options);
    }) as Resolver<TProductFormData>,
    defaultValues: {
      type: undefined,
      name: "",
      description: "",
      insurance_coverages: "",
      insurance_benefits: "",
      general_conditions: "",
      external_product_id: "",
      insurance_company_id: "",
      payment_gateway_id: "",
      policy_number: "",
      cod_sub: "",
      susep_code: "",
      type_of_vigency: "lifetime",
      capital_value: undefined,
      premium_value: undefined,
      iof_value: 0,
      min_permitted_age: undefined,
      max_permitted_age: undefined,
      commission_unique: undefined,
      pro_labore_unique: undefined,
      commission_distribution: [
        {
          agency: 0,
          commission: 0,
          pro_labore: 0,
          markup: 0,
          markup_mode: "percentage",
        },
      ],
      commission_lifetime_other_installments: 0,
      pro_labore_lifetime_other_installments: 0,
      policy_start_date: new Date(),
      policy_end_date: new Date(),
      policy_holder: "",
      insurance_policy_id: "",
      secondary_policy_holder: "",
      table_code: "",
      stipulant_code: "",
      sub_stipulant_code: "",
      insurance_policy_extra_price_code: "",
      code_plan: "",
      net_premium: undefined,
      capital_ranges: [
        {
          capital_value: 0,
          premium_value: 0,
          net_premium: 0,
          description: "",
          plan_name: "",
        },
      ],
    },
  });

  React.useEffect(() => {
    if (!product || product.id !== productId) {
      return;
    }

    const updatedAtRaw = (product as { updated_at?: string | Date }).updated_at;
    const updatedAt =
      updatedAtRaw instanceof Date
        ? updatedAtRaw.toISOString()
        : String(updatedAtRaw ?? "");
    const serverKey = `${product.id}:${updatedAt}`;
    if (lastHydratedServerKeyRef.current === serverKey) {
      return;
    }
    lastHydratedServerKeyRef.current = serverKey;

    if (product.type === EProductType.CREDIT) {
      productForm.reset({
        type: EProductType.CREDIT,
        name: product.name || "",
        insurance_company_id: product.insurance_company_id || "",
        payment_gateway_id: (product as { payment_gateway_id?: string })
          .payment_gateway_id || "",
        infinity_sync_as_source:
          (product as { infinity_sync_as_source?: boolean | null })
            .infinity_sync_as_source ?? true,
        external_product_id:
          (product as { external_product_id?: string | null })
            .external_product_id ?? "",
        credit_product_config: mergeCreditProductConfigFromApi(
          (product as { credit_product_config?: unknown })
            .credit_product_config,
        ),
      } as any);
      productForm.clearErrors();
      return;
    }

    if (product.type === EProductType.WARRANTY_INSURANCE) {
      productForm.reset({
        ...(product as Record<string, unknown>),
        type: EProductType.WARRANTY_INSURANCE,
      } as any);
      productForm.clearErrors();
      return;
    }

    if (product.type === EProductType.THEFT_INSURANCE) {
      productForm.reset({
        ...(product as Record<string, unknown>),
        type: EProductType.THEFT_INSURANCE,
      } as any);
      productForm.clearErrors();
      return;
    }

    if (
      product.type === EProductType.INSURANCE_ASSISTANCE &&
      getPanelBrandSlug() === "soulhub" &&
      !isInfinityAssistanceProduct(product)
    ) {
      const p = product as Record<string, unknown>;
      productForm.reset({
        ...p,
        type: EProductType.INSURANCE_ASSISTANCE,
        description: product.description ?? "",
        insurance_coverages: product.insurance_coverages ?? "",
        insurance_benefits: product.insurance_benefits ?? "",
        general_conditions: product.general_conditions ?? "",
        assistance_product_config: (p as { assistance_product_config?: unknown })
          .assistance_product_config,
        excluded_risks:
          (p as { excluded_risks?: string }).excluded_risks ?? "",
        policy_start_date: product.policy_start_date
          ? new Date(product.policy_start_date)
          : new Date(),
        policy_end_date: product.policy_end_date
          ? new Date(product.policy_end_date)
          : new Date(),
      } as any);
      productForm.clearErrors();
      return;
    }

    const isAssistance =
      (product.type as TProductFormData["type"]) ===
      EProductType.INSURANCE_ASSISTANCE;

    const requiredAssistanceFallback = isAssistance ? "N/A" : "";

    productForm.reset({
        name: product.name || "",
        description: product.description || "",
        type: (product.type as TProductFormData["type"]) || undefined,
        insurance_coverages: product.insurance_coverages || "",
        insurance_benefits: product.insurance_benefits || "",
        general_conditions: product.general_conditions || "",
        external_product_id:
          (product as { external_product_id?: string | null })
            .external_product_id ?? "",
        insurance_company_id: product.insurance_company_id || "",
        policy_number: product.policy_number || (isAssistance ? "N/A" : ""),
        cod_sub: product.cod_sub || "",
        susep_code: product.susep_code || (isAssistance ? "N/A" : ""),
        type_of_vigency:
          ((product as { type_of_vigency?: string }).type_of_vigency as TVigencyType) ||
          "annual",
        capital_value:
          product.capital_value === null ? undefined : product.capital_value,
        premium_value:
          product.premium_value === null ? undefined : product.premium_value,
        iof_value: product.iof_value ?? 0,
        min_permitted_age:
          product.min_permitted_age ?? (isAssistance ? 1 : undefined),
        max_permitted_age:
          product.max_permitted_age ?? (isAssistance ? 1 : undefined),
        commission_unique: product.commission_unique ?? undefined,
        pro_labore_unique: product.pro_labore_unique ?? undefined,
        commission_distribution:
          product.commission_distribution &&
          product.commission_distribution.length > 0
            ? product.commission_distribution.map((item: any) => ({
                agency: item.agency ?? 0,
                commission: item.commission ?? 0,
                pro_labore: item.pro_labore ?? 0,
                markup: item.markup ?? 0,
                markup_mode: item.markup_mode || "percentage",
              }))
            : [
                {
                  agency: 0,
                  commission: 0,
                  pro_labore: 0,
                  markup: 0,
                  markup_mode: "percentage",
                },
              ],
        commission_lifetime_other_installments:
          product.commission_lifetime_other_installments ?? undefined,
        pro_labore_lifetime_other_installments:
          product.pro_labore_lifetime_other_installments ?? undefined,
        policy_start_date: product.policy_start_date
          ? new Date(product.policy_start_date)
          : new Date(),
        policy_end_date: product.policy_end_date
          ? new Date(product.policy_end_date)
          : new Date(),
        policy_holder: product.policy_holder || "",
        insurance_policy_id: product.insurance_policy_id || "",
        secondary_policy_holder:
          product.secondary_policy_holder || requiredAssistanceFallback,
        table_code: product.table_code || requiredAssistanceFallback,
        stipulant_code: product.stipulant_code || requiredAssistanceFallback,
        sub_stipulant_code: product.sub_stipulant_code || requiredAssistanceFallback,
        insurance_policy_extra_price_code:
          product.insurance_policy_extra_price_code || "",
        code_plan: (product as any).code_plan || "",
        markup: (product as any).markup ?? 0,
        markup_mode: (product as any).markup_mode || "percentage",
        // NÃO popular capital_ranges aqui - deixar o useEffect do insurance-auto-form fazer a conversão
        // capital_ranges será convertido para plans automaticamente
        capital_ranges: (product.capital_ranges || []).map((cr: any) => {
          // Preservar TODOS os campos, seguindo o padrão dos outros produtos
          const mapped = {
            ...cr,  // ← Preserva TODOS os campos (id, position, etc)
            capital_value: cr.capital_value ?? 0,
            premium_value: cr.premium_value ?? 0,
            net_premium: cr.net_premium ?? 0,
            description: cr.description || "",
            markup: cr.markup ?? 0,
            markup_mode: cr.markup_mode || "percentage",
            code_plan: cr.code_plan || "",
            plan_name: cr.plan_name || "",
            net_price: cr.net_price ?? 0,
            minimum_price: cr.minimum_price ?? 0,
            maximum_price: cr.maximum_price ?? 0,
            commission_auto: cr.commission_auto !== null && cr.commission_auto !== undefined 
              ? Number(cr.commission_auto) 
              : undefined,
          };
          return mapped;
        }) || [
          {
            capital_value: 0,
            premium_value: 0,
            net_premium: 0,
            description: "",
            markup: 0,
            markup_mode: "percentage",
            net_price: 0,
            minimum_price: 0,
            maximum_price: 0,
            plan_name: "",
          },
        ],
        // Campos específicos do Auto/Moto
        km_max_permitted:
          product.type === EProductType.AUTO ||
          product.type === EProductType.MOTO
            ? (product as any).km_max_permitted ?? undefined
            : undefined,
        min_manufacturing_year:
          product.type === EProductType.AUTO ||
          product.type === EProductType.MOTO
            ? (product as any).min_manufacturing_year ?? undefined
            : undefined,
        vehicle_emission_type:
          (product as any).vehicle_emission_type ?? "apolice",
        is_collective_policy: (product as any).is_collective_policy ?? false,
        has_estipulante: (product as any).has_estipulante ?? false,
        has_sub_estipulante: (product as any).has_sub_estipulante ?? false,
        susep_product_code: (product as any).susep_product_code ?? "NA",
        has_km_limit: (product as any).has_km_limit ?? true,
        has_manufacturing_year_limit:
          (product as any).has_manufacturing_year_limit ?? true,
        max_vehicle_age: (product as any).max_vehicle_age ?? undefined,
        excluded_risks: (product as any).excluded_risks ?? "",
        premium_mode: (product as any).premium_mode ?? "calculated",
        prefixed_premium_value: (product as any).prefixed_premium_value,
        has_multiple_plans: (product as any).has_multiple_plans ?? true,
        vehicle_product_config: (() => {
          const raw = (product as any).vehicle_product_config;
          return {
            commercial: normalizeVehicleCommercialFromApi(raw?.commercial),
          };
        })(),
    });
    productForm.clearErrors();
  }, [product, productId, productForm]);

  const updateProductMutation = useMutation({
    mutationFn: (data: TProductFormData) => {
      return productsApi.updateProduct(productId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product", productId] });
      toast({
        title: t("generic.success"),
        description: t("products.success.productUpdated"),
      });
      navigate("/products");
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: `Erro ao atualizar produto: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateProduct = React.useCallback(
    (data: TProductFormData) => {
      if (data.type === EProductType.CREDIT) {
        updateProductMutation.mutate(data as any);
        return;
      }

      if (
        data.type === EProductType.AUTO ||
        data.type === EProductType.MOTO
      ) {
        updateProductMutation.mutate(data as any);
        return;
      }

      if (data.type === EProductType.WARRANTY_INSURANCE) {
        updateProductMutation.mutate(data as any);
        return;
      }

      if (data.type === EProductType.THEFT_INSURANCE) {
        updateProductMutation.mutate(data as any);
        return;
      }

      if (
        data.type === EProductType.INSURANCE_ASSISTANCE &&
        getPanelBrandSlug() === "soulhub"
      ) {
        updateProductMutation.mutate(data as any);
        return;
      }

      const isAssistance =
        data.type === EProductType.INSURANCE_ASSISTANCE;

      const requiredAssistanceFallback = isAssistance ? "N/A" : undefined;

      const payload: Record<string, unknown> = {
        type: data.type,
        name: data.name,
        description: data.description || undefined,
        insurance_coverages: data.insurance_coverages || undefined,
        insurance_benefits: data.insurance_benefits || undefined,
        general_conditions: data.general_conditions || undefined,
        insurance_company_id: data.insurance_company_id,
        policy_number:
          data.policy_number || (isAssistance ? "N/A" : undefined),
        cod_sub: data.cod_sub || undefined,
        susep_code: data.susep_code || (isAssistance ? "N/A" : undefined),
        type_of_vigency: data.type_of_vigency,
        iof_value: Number(data.iof_value || 0),
        min_permitted_age:
          (data as any).min_permitted_age !== undefined
            ? Number((data as any).min_permitted_age)
            : null,
        max_permitted_age:
          (data as any).max_permitted_age !== undefined
            ? Number((data as any).max_permitted_age)
            : null,
        policy_start_date: data.policy_start_date,
        policy_end_date: data.policy_end_date,
        insurance_policy_id: data.insurance_policy_id || undefined,
        secondary_policy_holder:
          data.secondary_policy_holder || requiredAssistanceFallback,
        stipulant_code: data.stipulant_code || requiredAssistanceFallback,
        sub_stipulant_code: data.sub_stipulant_code || requiredAssistanceFallback,
        table_code: data.table_code || requiredAssistanceFallback,
        capital_ranges: ((data as any).capital_ranges || []).map((cr: any) => {
          const mapped = {
            capital_value: cr.capital_value ?? 0,
            premium_value: cr.premium_value,
            net_price: cr.net_price,
            minimum_price: cr.minimum_price,
            maximum_price: cr.maximum_price,
            description: cr.description || "",
            code_plan: cr.code_plan || "",
            plan_name: cr.plan_name || "",
            net_premium: cr.net_premium,
            markup: cr.markup,
            markup_mode: cr.markup_mode || "percentage",
            commission_auto: cr.commission_auto,
          };
          return mapped;
        }),
        commission_distribution: (data.commission_distribution || []).map(
          (item) => ({
            agency: item.agency ?? 0,
            commission: item.commission ?? 0,
            pro_labore: item.pro_labore ?? 0,
            markup: item.markup,
            markup_mode: item.markup_mode || undefined,
          })
        ),
        policy_holder: data.policy_holder || undefined,
        insurance_policy_extra_price_code:
          data.insurance_policy_extra_price_code || undefined,
        code_plan: data.code_plan || undefined,
      };

      if (data.type_of_vigency === "lifetime") {
        Object.assign(payload, {
          commission_lifetime_other_installments:
            data.commission_lifetime_other_installments,
          pro_labore_lifetime_other_installments:
            data.pro_labore_lifetime_other_installments,
        });
      }

      if (data.type_of_vigency === "annual") {
        Object.assign(payload, {
          commission_unique: data.commission_unique,
          pro_labore_unique: data.pro_labore_unique,
        });
      }

      payload.markup = data.markup;
      payload.markup_mode = data.markup_mode || undefined;
      
      updateProductMutation.mutate(payload as unknown as TProductFormData);
    },
    [updateProductMutation]
  );

  return {
    product,
    isLoading,
    isError,
    updateProduct,
    productForm,
  };
};
